import React, { useCallback, useMemo } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import {
  Box,
  Button,
  Paper,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  Typography
} from '@mui/material'
import {
  Add as AddIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material'
import { apiClient } from '../../services/api'
import { useDataFetching } from '../../hooks/useDataFetching'
import { useListPageState } from '../../hooks/useListPageState'
import AdminPageHeader from '../../components/admin/shared/AdminPageHeader'
import BaseDataTable, { TableColumn } from '../../components/shared/BaseDataTable'
import { poolwerxColors } from '../../theme/colors'
import { getMarketChipColor } from '../../utils/marketChip'

interface DefaultBDMInfo {
  id: number
  full_name: string
  email: string
}

interface Region {
  id: number
  region_code: string
  region_name: string
  market: string
  description?: string
  default_bdm_id?: number
  active_fp_count: number
  default_bdm?: DefaultBDMInfo
  created_at: string
  updated_at: string
}

interface RegionListResponse {
  regions: Region[]
  total: number
  page: number
  per_page: number
  pages: number
}

const RegionList: React.FC = () => {
  const { state, updateField, updateFields } = useListPageState({
    defaultSortField: 'region_code',
    defaultPerPage: 100
  })

  const fetchRegions = useCallback(async () => {
    const response = await apiClient.get('/admin/regions?per_page=100')
    return response.data as RegionListResponse
  }, [])

  const { data, loading, error, refetch } = useDataFetching<RegionListResponse>({
    fetchFn: fetchRegions,
    dependencies: [],
    immediate: true
  })

  const filteredRegions = useMemo(() => {
    let filtered = data?.regions ?? []

    const searchText = (state.search as string)?.toLowerCase()
    if (searchText) {
      filtered = filtered.filter(region =>
        region.region_name.toLowerCase().includes(searchText) ||
        region.region_code.toLowerCase().includes(searchText)
      )
    }

    if (state.market) {
      filtered = filtered.filter(region => region.market === state.market)
    }

    const { sortField, sortDirection } = state
    return [...filtered].sort((a, b) => {
      const aVal = (a as any)[sortField] ?? ''
      const bVal = (b as any)[sortField] ?? ''
      const cmp = typeof aVal === 'string'
        ? aVal.localeCompare(bVal)
        : aVal < bVal ? -1 : aVal > bVal ? 1 : 0
      return sortDirection === 'desc' ? -cmp : cmp
    })
  }, [data, state.search, state.market, state.sortField, state.sortDirection])

  const columns: TableColumn<Region>[] = [
    {
      key: 'region_code',
      label: 'Region Code',
      sortable: true,
      render: (region) => (
        <Typography variant="body2" fontWeight="medium" fontFamily="monospace">
          {region.region_code}
        </Typography>
      )
    },
    {
      key: 'region_name',
      label: 'Region Name',
      sortable: true,
      render: (region) => (
        <>
          <Typography variant="body2">{region.region_name}</Typography>
          {region.description && (
            <Typography variant="caption" color="textSecondary" display="block">
              {region.description}
            </Typography>
          )}
        </>
      )
    },
    {
      key: 'market',
      label: 'Market',
      sortable: true,
      render: (region) => (
        <Chip label={region.market} size="small" color={getMarketChipColor(region.market)} />
      )
    },
    {
      key: 'default_bdm',
      label: 'Default ROM/FBC',
      render: (region) => (
        region.default_bdm ? (
          <Box>
            <Typography variant="body2">{region.default_bdm.full_name}</Typography>
            <Typography variant="caption" color="textSecondary">
              {region.default_bdm.email}
            </Typography>
          </Box>
        ) : (
          <Typography variant="body2" color="textSecondary">Not set</Typography>
        )
      )
    },
    {
      key: 'active_fp_count',
      label: 'Active FPs',
      align: 'center',
      sortable: true,
      render: (region) => (
        <Chip
          label={region.active_fp_count}
          size="small"
          color={region.active_fp_count > 0 ? 'info' : 'default'}
        />
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      align: 'right',
      render: (region) => (
        <IconButton
          component={RouterLink}
          to={`/admin/system/regions/${region.region_code}/edit`}
          size="small"
          title="Edit"
        >
          <EditIcon fontSize="small" />
        </IconButton>
      )
    }
  ]

  return (
    <>
      <AdminPageHeader
        pageKey="regionManagement"
        actions={
          <Stack direction="row" spacing={2}>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={refetch}>
              Refresh
            </Button>
            <Button
              component={RouterLink}
              to="/admin/system/regions/new"
              variant="contained"
              startIcon={<AddIcon />}
              sx={{
                backgroundColor: poolwerxColors.teal,
                '&:hover': { backgroundColor: poolwerxColors.tealHover }
              }}
            >
              Add Region
            </Button>
          </Stack>
        }
      />

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            size="small"
            placeholder="Search by region name or code..."
            value={state.search}
            onChange={(e) => updateField('search', e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 250 }}
          />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Market</InputLabel>
            <Select
              value={state.market || ''}
              label="Market"
              onChange={(e) => updateField('market', e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="ANZ">ANZ</MenuItem>
              <MenuItem value="USA">USA</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      <BaseDataTable
        variant="enhanced"
        columns={columns}
        data={filteredRegions}
        loading={loading}
        error={error}
        sortField={state.sortField}
        sortDirection={state.sortDirection}
        onSort={(field, direction) => updateFields({ sortField: field, sortDirection: direction })}
        showCount
        countLabel="regions"
        emptyMessage="No regions found"
        getRowKey={(region) => region.id}
      />
    </>
  )
}

export default RegionList
